import { Fragment, useContext, useEffect, useState } from "react";
import { useParams } from "react-router";

import { IParentData } from "../../../analytics/pages/report-details/report-details";
import NormalTable, { ITableRowData, IColumn, IScale } from "../../../shared/components/normal-table/normal-table";
import Spinner from "../../../shared/components/spinner/spinner";
import { IBlock } from "../../../shared/components/widgets-layout/widgets-layout";
import AuthContext from "../../../shared/context/auth-context";
import useHttp from "../../../shared/hooks/use-http";
import {useHttpNonTable} from "../../../shared/hooks/use-httpNonTable";
import { getAllDataNonTable, IListPageData, postSingleNonTable } from "../../../shared/utils/api";
import { validateLoadingStatusNew, transformObjectsFracs } from "../../../shared/utils/utils";

export interface IRowStatus {
  opt_thresh_id?: string,
  approved_id?: string
}

export interface ITableConfig {
    columns: IColumn[];
    unblockInput?: boolean;
    allColumns?: any; // eslint-disable-line
    dataUrl?: string;
    fetchedData?: IListPageData | undefined;
    hideButtons: string[];
    allStatus?: string | null;
    hideSearchInput?: boolean;
    allStatusDelete?: string | null;
    hideCheckBoxes?: boolean;
    timeframeStartTilEnd?: boolean;
    clickRowToSelectMode?: boolean;
    scale?: IScale;
    lock?: boolean;
    widgetData?: IBlock;
    selectedThreshold?: string;
    selectedSource?: string;
    defaultSortCol?: string;
    orderSettings?: IParentData;
    initiallySelectedRowsIds?: string[];
    draggingStarted?: boolean;
    statusFields?: IRowStatus;
    getResultDataByColsStatus?: string | null | undefined;
    widgetId?: string;
    tobeDeselected?: string[];
    lastChangedWidget?: ITableRowData;
    verticallyHighlighted?: string[];
    onlySelectedThreshold?: boolean;
    getDraggingStatus?: (toggle: boolean) => void;
    getSelectedRowsData?: (data: ITableRowData[]) => void;
    handleOnDelete?: (ids: string[]) => void;
    onRowClickAction?: (id: string) => void;
    getNewColumnsOrder?: (data: IParentData, order: IColumn[]) => void;
    renderExtraColumns?: (id: string) => JSX.Element;
  }

const NormalTableHoc = ({ columns, widgetData, lock, selectedThreshold, onlySelectedThreshold, allColumns, widgetId, tobeDeselected, lastChangedWidget, getResultDataByColsStatus, verticallyHighlighted, initiallySelectedRowsIds, orderSettings, statusFields, getDraggingStatus, draggingStarted, getNewColumnsOrder, selectedSource, scale, defaultSortCol, allStatus, hideButtons, clickRowToSelectMode, hideCheckBoxes, hideSearchInput, unblockInput, getSelectedRowsData, dataUrl, fetchedData }: ITableConfig) => {
  const [lastSortedBy, setLastSortedBy] = useState("");
  const [defaultCols, setDefaultCols] = useState<ITableRowData[] | undefined>(undefined);
  const [resultsGlobalData, setResultsGlobalData] = useState<undefined | ITableRowData[]>(undefined);
  const [historyGlobalData, setHistorysGlobalData] = useState<undefined | ITableRowData[]>(undefined);
  
  const authCtx = useContext(AuthContext);
  const params = useParams();

  const { sendRequest: getModelDataSortBy, status: getModelDataStatus, data: modelData } = useHttpNonTable(getAllDataNonTable, false, authCtx.token, undefined, "analytics");
  
  const { sendRequest: getReportDataSortBy, status: getReportDataStatus, data: reportData, error: reportDataError } = useHttp(postSingleNonTable, false, authCtx.token, undefined, "analytics");
  const { sendRequest: getReportDataSortBySort, status: getReportDataSortBySortStatus, data: getReportDataSortBySortData, error: getReportDataSortBySortError } = useHttp(postSingleNonTable, false, authCtx.token, undefined, "analytics");

  const { sendRequest: getHistoryDataSortBy, status: getHistoryDataStatus, data: getHistoryDataData, error: getHistoryDataSortByError } = useHttpNonTable(postSingleNonTable, false, authCtx.token, undefined, "analytics");

  const { sendRequest: getSourceDataSortBy, status: getSourceDataStatus, data: sourceData } = useHttpNonTable(getAllDataNonTable, false, authCtx.token, undefined, "analytics");

  useEffect(() => {
    if (selectedSource === "history") {
      const excludeThresholdAndNonExisting = columns.filter((colData) => allColumns[`${colData.key}`]);
      // threshold and non matching(old) keys will be filtered out when checked against all colums
      const allActCols = excludeThresholdAndNonExisting.map(col => col.key);

      if (selectedThreshold !== undefined) {
        getHistoryDataSortBy(`history_trend/${params.id}${lastSortedBy ? lastSortedBy : "?order_by=start_date asc"}`, {
          columns: allActCols,
          threshold: selectedThreshold
        }, undefined, "none");
      }
    }

  }, [widgetData]);

  useEffect(() => {
    if (onlySelectedThreshold && selectedSource === "report_data" && allColumns && columns?.length > 0) {
      const excludeThresholdAndNonExisting = columns.filter((colData) => allColumns[`${colData.key}`]);
      const allActCols = excludeThresholdAndNonExisting.map(col => col.key);
      getReportDataSortBySort(`report_data/${params.id}${lastSortedBy ? lastSortedBy : "?order_by=threshold asc"}`, {
        columns: allActCols,
        onlySelectedThreshold: true
      }, undefined, "none");
    }
  }, [allColumns]);

  useEffect(() => {
    if (widgetId && localStorage.getItem(widgetId) !== "undefined") {
      const dataLoc = localStorage.getItem(widgetId);
      dataLoc && sortBy(dataLoc);
    }
  }, []);

  useEffect(() => {
    if (getHistoryDataStatus === "completed" && !getHistoryDataSortByError && getHistoryDataData) {
      const result = getHistoryDataData.data.map((resData:ITableRowData) => {
        return {...resData, id: resData.threshold};
      });
      setHistorysGlobalData(result);
    }
  }, [getHistoryDataStatus]);

  const sortBy = (sortingQuery: string) => {
    if (selectedSource === "report_data" && allColumns) {
      widgetId && localStorage.setItem(widgetId, sortingQuery);
      
      setLastSortedBy(sortingQuery);
      const excludeThresholdAndNonExisting = columns.filter((colData) => allColumns[`${colData.key}`]);

      getReportDataSortBySort(`report_data/${params.id}${sortingQuery}`, {
        columns: excludeThresholdAndNonExisting.map((col) => col.key),
        onlySelectedThreshold: onlySelectedThreshold
      }, undefined, "none");
    }
    if (selectedSource === "model") {
      getModelDataSortBy(`model/${params.id}`, `${sortingQuery}`, undefined, "none");
    }
    if (selectedSource === "source_data") {
      getSourceDataSortBy(`source_data/${params.id}`, `${sortingQuery}`, undefined, "none");
    }
    if (selectedSource === "history") {
      const excludeThresholdAndNonExisting = columns.filter((colData) => allColumns[`${colData.key}`]);
      // threshold and non matching(old) keys will be filtered out when checked against all colums
      const allActCols = excludeThresholdAndNonExisting.map(col => col.key);

      if (selectedThreshold !== undefined) {
        getHistoryDataSortBy(`history_trend/${params.id}${sortingQuery ? sortingQuery : "?order_by=start_date asc"}`, {
          columns: allActCols,
          threshold: selectedThreshold
        }, undefined, "none");
      }
    }
  };

  useEffect(() => {
    if (getReportDataSortBySortStatus === "completed" && !getReportDataSortBySortError && getReportDataSortBySortData) {
      const result = getReportDataSortBySortData.map((resData:ITableRowData) => {
        return {...resData, id: resData.threshold};
      });
      setResultsGlobalData(result);
    }
  }, [getReportDataSortBySortStatus]);

  useEffect(() => {
    if (getReportDataStatus === "completed" && !reportDataError && reportData) {
      const result = reportData.map((resData:ITableRowData, i:number) => {
        return {...resData, ...defaultCols?.[i], id: resData.threshold};
      });
      setResultsGlobalData(result);
    }
  }, [getReportDataStatus]);

  useEffect(() => {
    if (lastChangedWidget && widgetId === lastChangedWidget?.id && getReportDataStatus !== "pending" && getResultDataByColsStatus !== "pending" && allColumns) {
      const excludeThresholdAndNonExisting = columns.filter((colData) => allColumns[`${colData.key}`]);
      // threshold and non matching(old) keys will be filtered out when checked against all colums
      const allActCols = excludeThresholdAndNonExisting.map(col => col.key);

      if (selectedSource === "report_data") {
        getReportDataSortBy(`report_data/${params.id}${lastSortedBy ? lastSortedBy : ""}`, {
          columns: allActCols,
          onlySelectedThreshold: onlySelectedThreshold
        }, undefined, "none");
      }
    }
  }, [lastChangedWidget?.changed]);

  useEffect(() => {
    if (fetchedData?.rows?.length !== 0 && !defaultCols) {
      setDefaultCols(fetchedData?.rows);
    }
  }, [fetchedData]);

  const renderTable = () => {
    const constructData: any = { // eslint-disable-line
      model: fetchedData && !modelData ? (fetchedData.rows? fetchedData.rows : modelData) : modelData ? modelData : [],
      source_data: sourceData ? sourceData : [],
      report_data: resultsGlobalData ? resultsGlobalData : [],
      history: historyGlobalData? historyGlobalData : [],
    };
    let preparedResults = [...constructData[`${selectedSource}`]];

    if (selectedSource === "report_data") {
      preparedResults = preparedResults.map(row => {
        const prepareResult = transformObjectsFracs(row);
        return {
          approved: statusFields && statusFields.approved_id? (statusFields.approved_id === row.id? true: false) : false,
          recommended: statusFields && statusFields.approved_id? (statusFields.approved_id === row.id? true: false) : false,
          id: row.threshold,
          ...prepareResult
        };
      });
    }

    let prepallColumns:IColumn[] = [];
    if (selectedSource === "model" || selectedSource === "source_data" || selectedSource === "history") {
      prepallColumns =  columns?.filter((col:IColumn) => col.key !== "threshold");
    } else {
      prepallColumns = columns;
    }

    return (
      <Fragment>
        {validateLoadingStatusNew([getModelDataStatus, getReportDataSortBySortStatus, getReportDataStatus, getHistoryDataStatus, getSourceDataStatus]) && <Spinner />}
        <NormalTable
          sortBy={sortBy}
          draggingStarted={draggingStarted}
          getDraggingStatus={getDraggingStatus}
          columns={prepallColumns}
          verticallyHighlighted={verticallyHighlighted}
          fetchedData={preparedResults.length === 0 && !onlySelectedThreshold ? fetchedData : { rows: preparedResults }}
          allStatus={allStatus}
          dataUrl={dataUrl}
          orderSettings={orderSettings}
          editColumsOrder={!lock}
          timeFrameIsAstring={true}
          getNewColumnsOrder={getNewColumnsOrder}
          defaultSortCol={defaultSortCol}
          tobeDeselected={tobeDeselected}
          hideSearchInput={hideSearchInput}
          initialySelectedRows={initiallySelectedRowsIds}
          hideCheckBoxes={hideCheckBoxes}
          getSelectedRowsData={getSelectedRowsData}
          unblockInput={unblockInput}
          clickRowToSelectMode={clickRowToSelectMode}
          hideButtons={hideButtons}
          scale={scale}
        />
      </Fragment>
    );
  };

  return <Fragment>{renderTable()}</Fragment>;
};

export default NormalTableHoc;